import axios from 'axios';
import type { 
  SearchRequest, 
  SearchResponse, 
  Candidate, 
  ApiResponse 
} from '../shared/types';
import { localSearchAPI, localCandidatesAPI, localHealthAPI } from './localApi';

// Configuration for API mode
const API_MODE = process.env.REACT_APP_API_MODE || 'server'; // 'server' or 'cloud-functions'
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const CLOUD_FUNCTIONS_BASE_URL = process.env.REACT_APP_CLOUD_FUNCTIONS_URL || '';

// Create axios instance based on mode
const createAPIClient = () => {
  if (API_MODE === 'cloud-functions') {
    return axios.create({
      baseURL: CLOUD_FUNCTIONS_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  } else {
    return axios.create({
      baseURL: `${API_BASE_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
};

const api = createAPIClient();

// Response interceptor to handle API responses
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error('API Error:', error);
    throw error.response?.data || error.message;
  }
);

// Helper function to get endpoint URL based on mode
const getEndpoint = (path: string): string => {
  if (API_MODE === 'cloud-functions') {
    // Map server endpoints to cloud function names
    const endpointMap: Record<string, string> = {
      '/search/candidates': '/searchCandidates',
      '/search/test': '/testSearch',
      '/candidates': '/getCandidates',
      '/candidates/skills/list': '/getSkillsList',
      '/candidates/locations/list': '/getLocationsList',
      '/health': '/health'
    };
    
    // For candidate by ID, extract the ID and use getCandidateById
    if (path.startsWith('/candidates/') && !path.includes('/skills/') && !path.includes('/locations/')) {
      return `/getCandidateById`;
    }
    
    return endpointMap[path] || path;
  }
  return path;
};

// Dynamic API functions that can switch between local and external
// Default to LOCAL when the UI toggle is hidden. Can be overridden with REACT_APP_USE_LOCAL_API.
const DEFAULT_LOCAL_API = (process.env.REACT_APP_USE_LOCAL_API ?? 'true') === 'true';
let useLocalAPI = DEFAULT_LOCAL_API;

export const setApiMode = (local: boolean) => {
  useLocalAPI = local;
  console.log(`🔧 API Mode switched to: ${local ? 'LOCAL FRONTEND' : 'EXTERNAL'}`);
};

export const searchAPI = {
  searchCandidates: async (searchRequest: SearchRequest): Promise<ApiResponse<SearchResponse>> => {
    if (useLocalAPI) {
      return localSearchAPI.searchCandidates(searchRequest);
    }
    return api.post(getEndpoint('/search/candidates'), searchRequest);
  },

  testSearch: async (): Promise<ApiResponse<SearchResponse>> => {
    if (useLocalAPI) {
      return localSearchAPI.testSearch();
    }
    return api.get(getEndpoint('/search/test'));
  },
};

export const candidatesAPI = {
  getAllCandidates: async (params?: {
    limit?: number;
    offset?: number;
    skills?: string;
    location?: string;
  }): Promise<ApiResponse<{
    candidates: Candidate[];
    total: number;
    limit: number;
    offset: number;
  }>> => {
    if (useLocalAPI) {
      return localCandidatesAPI.getAllCandidates(params);
    }
    return api.get(getEndpoint('/candidates'), { params });
  },

  getCandidateById: async (id: string): Promise<ApiResponse<Candidate>> => {
    if (useLocalAPI) {
      return localCandidatesAPI.getCandidateById(id);
    }
    if (API_MODE === 'cloud-functions') {
      // For cloud functions, append the ID to the URL path
      return api.get(`${getEndpoint('/candidates/' + id)}/${id}`);
    } else {
      return api.get(`/candidates/${id}`);
    }
  },

  getUniqueSkills: async (): Promise<ApiResponse<string[]>> => {
    if (useLocalAPI) {
      return localCandidatesAPI.getUniqueSkills();
    }
    return api.get(getEndpoint('/candidates/skills/list'));
  },

  getUniqueLocations: async (): Promise<ApiResponse<string[]>> => {
    if (useLocalAPI) {
      return localCandidatesAPI.getUniqueLocations();
    }
    return api.get(getEndpoint('/candidates/locations/list'));
  },
};

export const healthAPI = {
  checkHealth: async (): Promise<any> => {
    if (useLocalAPI) {
      return localHealthAPI.checkHealth();
    }
    return api.get(getEndpoint('/health'));
  },
};

// Log the current API configuration
console.log(`🔧 API Mode: ${API_MODE}`);
if (API_MODE === 'cloud-functions') {
  console.log(`☁️  Cloud Functions URL: ${CLOUD_FUNCTIONS_BASE_URL}`);
} else {
  console.log(`🖥️  Server URL: ${API_BASE_URL}`);
}
